// src/pages/Login.jsx
import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import Logo from "../assets/PNG.png";

const db = getFirestore();
const functions = getFunctions(undefined, "southamerica-east1");
const atualizarMinhasClaims = httpsCallable(functions, "atualizarMinhasClaims");

/* ---------------- SweetAlert theme (GestorPro) ---------------- */
const swalGP = Swal.mixin({
  buttonsStyling: false,
  customClass: {
    popup: "gp-swal-popup",
    title: "gp-swal-title",
    htmlContainer: "gp-swal-text",
    input: "gp-swal-input",
    confirmButton: "gp-swal-confirm",
    cancelButton: "gp-swal-cancel",
    actions: "gp-swal-actions",
    validationMessage: "gp-swal-validation",
  },
});

function humanizeAuthError(err) {
  const code = err?.code || "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-email":
      return "E-mail ou senha incorretos, ou usuário inexistente.";
    case "auth/too-many-requests":
      return "Muitas tentativas. Tente novamente em alguns minutos.";
    case "auth/network-request-failed":
      return "Falha de rede. Verifique sua conexão.";
    case "functions/unauthenticated":
      return "Sessão expirada ao consultar o servidor. Entre novamente.";
    default:
      return err?.message || "Não foi possível concluir a ação.";
  }
}

const Login = () => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const app = auth.app;
      const projectId = app?.options?.projectId;
      const apiKey = app?.options?.apiKey || "";
      const apiKeyMasked = apiKey ? `${apiKey.slice(0, 6)}…${apiKey.slice(-4)}` : "(sem apiKey)";
      console.info("[Login] Firebase app:", { projectId, apiKey: apiKeyMasked });
    } catch {}
  }, []);

  const garantirClaimEmpresa = async (uid) => {
    try {
      await auth.currentUser?.getIdToken(true);
      let tokenResult = await auth.currentUser?.getIdTokenResult();
      let empresaId = tokenResult?.claims?.empresaId;

      if (empresaId) {
        localStorage.setItem("empresaId", empresaId);
        return true;
      }

      try {
        await atualizarMinhasClaims();
      } catch {}

      await auth.currentUser?.getIdToken(true);
      tokenResult = await auth.currentUser?.getIdTokenResult();
      empresaId = tokenResult?.claims?.empresaId;

      if (empresaId) {
        localStorage.setItem("empresaId", empresaId);
        return true;
      }

      try {
        const snap = await getDoc(doc(db, "users", uid));
        const empresaIdPerfil = snap.exists() ? snap.data()?.empresaId : null;
        if (empresaIdPerfil) {
          localStorage.setItem("empresaId", empresaIdPerfil);
          return true;
        }
      } catch {}
    } finally {}
    return false;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro("");
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), senha);
      await garantirClaimEmpresa(cred.user.uid);
      navigate("/");
    } catch (error) {
      setErro(humanizeAuthError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEsqueciSenha = async () => {
    if (resetLoading) return;

    const { value: emailInput, isConfirmed } = await swalGP.fire({
      icon: "info",
      title: "Redefinir senha",
      text: "Informe o e-mail da sua conta para enviarmos o link de redefinição.",
      input: "email",
      inputLabel: "E-mail",
      inputPlaceholder: "seuemail@dominio.com",
      inputValue: (email || "").trim(),
      showCancelButton: true,
      confirmButtonText: "Enviar link",
      cancelButtonText: "Cancelar",
      showLoaderOnConfirm: true,
      preConfirm: async (value) => {
        const v = (value || "").trim();
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
        if (!ok) {
          Swal.showValidationMessage("Digite um e-mail válido.");
          return;
        }

        try {
          setResetLoading(true);
          await sendPasswordResetEmail(auth, v);
          return v;
        } catch (err) {
          Swal.showValidationMessage(humanizeAuthError(err));
          return;
        } finally {
          setResetLoading(false);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (!isConfirmed) return;

    // se enviou com sucesso, já preenche o campo do login
    if (emailInput) setEmail(emailInput);

    await swalGP.fire({
      icon: "success",
      title: "Link enviado!",
      text: "Se esse e-mail estiver cadastrado, você receberá um link para redefinir sua senha.",
      confirmButtonText: "OK",
    });
  };

  return (
    <div className="h-screen w-screen overflow-hidden flex items-center justify-center bg-gradient-to-br from-gestor-blue/5 via-gestor-blue/10 to-white">
      {/* Cartão “quadrado” (altura fixa) */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gestor-blue/10 animate-fade-in">
        <div className="flex flex-col items-center p-8 sm:p-10 h-[560px]">
          {/* LOGO maior */}
          <div className="h-28 sm:h-32 md:h-36 lg:h-40 mb-6 flex items-center justify-center">
            <img
              src={Logo}
              alt="GestorPro"
              className="max-h-full w-auto select-none pointer-events-none object-contain"
              draggable="false"
            />
          </div>

          {erro && <p className="text-red-500 mb-4 animate-shake">{erro}</p>}

          <form className="w-full flex-1" onSubmit={handleLogin}>
            <label className="block text-sm font-semibold text-gray-600 mb-1">E-mail</label>
            <input
              type="email"
              placeholder="Seu e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mb-4 w-full p-3 rounded-lg border border-gray-200 focus:border-gestor-blue focus:ring-2 focus:ring-gestor-orange/30 bg-white text-gray-900 placeholder-gray-400 outline-none transition"
              autoComplete="username"
              required
            />

            <label className="block text-sm font-semibold text-gray-600 mb-1">Senha</label>
            <div className="relative">
              <input
                type={showSenha ? "text" : "password"}
                placeholder="Sua senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="mb-4 w-full p-3 rounded-lg border border-gray-200 focus:border-gestor-blue focus:ring-2 focus:ring-gestor-orange/30 bg-white text-gray-900 placeholder-gray-400 outline-none transition"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowSenha((prev) => !prev)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gestor-blue"
                tabIndex={-1}
                aria-label={showSenha ? "Ocultar senha" : "Mostrar senha"}
                title={showSenha ? "Ocultar" : "Mostrar"}
              >
                {showSenha ? (
                  <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" strokeWidth="2" d="M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z" />
                    <path stroke="currentColor" strokeWidth="2" d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                  </svg>
                ) : (
                  <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" strokeWidth="2" d="m3 3 18 18M10.7 10.7A2 2 0 0 0 12 14a2 2 0 0 0 1.3-3.3" />
                    <path stroke="currentColor" strokeWidth="2" d="M17.94 17.94C16.15 19.26 14.14 20 12 20c-6.5 0-10-7-10-7 1.04-2.07 2.67-4.41 4.95-6.37M20.29 17.29c1.34-1.28 2.45-2.7 3.26-4.08C21.97 9.18 18.52 4 12 4c-2.16 0-4.16.73-5.95 2.06" />
                  </svg>
                )}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg mt-2 text-white font-bold shadow transition
                ${loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "!bg-gestor-blue hover:bg-gestor-blue/90 focus:ring-2 focus:ring-gestor-blue/20 border-0"}`}
            >
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </form>

          {/* ✅ moveu para o rodapé, acima do "Cadastre-se" */}
          <button
            type="button"
            onClick={handleEsqueciSenha}
            disabled={resetLoading}
            className={`mt-4 text-center text-xs font-semibold text-gestor-blue hover:underline transition
              ${resetLoading ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {resetLoading ? "Aguarde..." : "Esqueci minha senha"}
          </button>

          <p className="mt-2 text-center text-xs text-gray-500">
            Ainda não tem conta?{" "}
            <Link to="/register" className="text-gestor-blue font-semibold hover:underline">
              Cadastre-se
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.7s cubic-bezier(.39,.575,.565,1) both; }
        @keyframes fadeIn { 0%{opacity:0;transform:translateY(30px)} 100%{opacity:1;transform:translateY(0)} }
        .animate-shake { animation: shake 0.15s 2; }
        @keyframes shake {
          0%{transform:translateX(0);}
          25%{transform:translateX(-8px);}
          50%{transform:translateX(8px);}
          75%{transform:translateX(-8px);}
          100%{transform:translateX(0);}
        }

        /* ===== SweetAlert2 theme (GestorPro) ===== */
        .gp-swal-popup{
          border-radius: 16px !important;
          padding: 22px 22px 18px !important;
          box-shadow: 0 20px 60px rgba(0,0,0,.18) !important;
          border: 1px solid rgba(0, 64, 114, 0.12) !important; /* #004072 */
        }
        .gp-swal-title{
          font-size: 18px !important;
          font-weight: 800 !important;
          color: #111827 !important;
          margin: 0 !important;
        }
        .gp-swal-text{
          color: #4b5563 !important;
          font-size: 14px !important;
          margin-top: 8px !important;
        }
        .gp-swal-input{
          margin-top: 10px !important;
          border-radius: 12px !important;
          border: 1px solid #e5e7eb !important;
          padding: 12px 12px !important;
          font-size: 14px !important;
          box-shadow: none !important;
        }
        .gp-swal-input:focus{
          border-color: rgba(0, 64, 114, .55) !important;
          box-shadow: 0 0 0 3px rgba(0, 74, 173, .16) !important;
        }
        .gp-swal-actions{
          gap: 10px !important;
          margin-top: 16px !important;
        }
        .gp-swal-confirm{
          background: #004072 !important;
          color: #fff !important;
          border: 0 !important;
          border-radius: 12px !important;
          padding: 10px 14px !important;
          font-weight: 800 !important;
          font-size: 14px !important;
        }
        .gp-swal-confirm:hover{
          filter: brightness(1.08) !important;
        }
        .gp-swal-confirm:focus{
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(0, 74, 173, .18) !important;
        }
        .gp-swal-cancel{
          background: #f3f4f6 !important;
          color: #111827 !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 12px !important;
          padding: 10px 14px !important;
          font-weight: 800 !important;
          font-size: 14px !important;
        }
        .gp-swal-cancel:hover{
          background: #e5e7eb !important;
        }
        .gp-swal-validation{
          background: rgba(244, 63, 94, .08) !important;
          color: rgb(190, 18, 60) !important;
          border: 1px solid rgba(244, 63, 94, .22) !important;
          border-radius: 12px !important;
          padding: 10px 12px !important;
          margin-top: 10px !important;
          font-size: 13px !important;
        }
      `}</style>
    </div>
  );
};

export default Login;
